import { useMemo, useState, type ComponentProps, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOwnerPropertiesContractGate } from "@/hooks/v2/useProperties";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight, Download, Info } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  LabelList,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useOwnerPortalV3 } from "@/hooks/owner/useOwnerPortalV3";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { PAGE_TITLE_CLASS } from "@/components/admin/v2/page-title";

type RevenueRange = "month" | "quarter" | "year";

type PortfolioRow = {
  property_id: number;
  title: string;
  city: string;
  occupied_rooms: number;
  total_rooms: number;
  occupancy_pct: number;
  revenue_period_cents: number;
  expected_rent_cents: number;
  collection_rate_pct: number | null;
  /** Rent due in the period that is not yet paid (20261021195000). */
  open_cents: number;
  open_tenants: number;
  /** The part of `open_cents` already past its due date. */
  overdue_cents: number;
  overdue_tenants: number;
  yield_pct: number | null;
};

type PortfolioPayload = {
  kpis: {
    rent_collected_cents: number;
    occupancy_pct: number;
    occupied_rooms: number;
    total_rooms: number;
    collection_rate_pct: number | null;
    expected_rent_cents: number;
    /** Rent due in the period that is not yet paid: expected - collected. */
    open_cents: number;
    open_tenants: number;
    avg_yield_pct: number | null;
    /** The part of `open_cents` already past its due date. */
    overdue_cents: number;
    /** Tenants behind on rent — counted off the same rows overdue_cents sums. */
    overdue_tenants: number;
    range: RevenueRange;
    /** First day of the month this payload covers (20261012085004). Absent
     *  against a database that predates the migration. */
    period_month?: string;
  };
  /** Rent only, one entry per DUE month. `rent_cents` is the part of that
   *  month's rent that has been paid, whenever it was paid. Deposits are the
   *  tenant''s money held in trust, so the RPC stopped returning a deposit
   *  series — see 20260815120000. */
  monthly: {
    month: string;
    rent_cents: number;
    due_cents: number;
    open_cents: number;
  }[];
  properties: PortfolioRow[];
  property_count: number;
};

/**
 * A payload from a database that has not yet applied 20261021195000 carries no
 * open / due figures. The frontend and the migration deploy separately, so for
 * that window derive them from what is there (open = expected - collected, the
 * same identity the ledger holds) instead of rendering NaN.
 */
function normalizePortfolio(
  raw: PortfolioPayload | null
): PortfolioPayload | null {
  if (!raw) return raw;
  const num = (v: number | null | undefined) => v ?? 0;
  const open = (expected: number, collected: number) =>
    Math.max(expected - collected, 0);
  return {
    ...raw,
    kpis: {
      ...raw.kpis,
      expected_rent_cents: num(raw.kpis.expected_rent_cents),
      open_cents:
        raw.kpis.open_cents ??
        open(
          num(raw.kpis.expected_rent_cents),
          num(raw.kpis.rent_collected_cents)
        ),
      open_tenants: raw.kpis.open_tenants ?? num(raw.kpis.overdue_tenants),
      overdue_cents: num(raw.kpis.overdue_cents),
      overdue_tenants: num(raw.kpis.overdue_tenants),
    },
    monthly: (raw.monthly ?? []).map((m) => ({
      ...m,
      due_cents: m.due_cents ?? m.rent_cents,
      open_cents: m.open_cents ?? 0,
    })),
    // Per-property rows too: the develop line's previous RPC returned no
    // expected / overdue figures per property at all.
    properties: (raw.properties ?? []).map((r) => {
      const expected = num(r.expected_rent_cents);
      return {
        ...r,
        expected_rent_cents: expected,
        open_cents: r.open_cents ?? open(expected, num(r.revenue_period_cents)),
        open_tenants: r.open_tenants ?? num(r.overdue_tenants),
        overdue_cents: num(r.overdue_cents),
        overdue_tenants: num(r.overdue_tenants),
      };
    }),
  };
}

/** The hatch that marks open rent, in the chart legend and on the bars. */
const OPEN_HATCH_CSS =
  "repeating-linear-gradient(-45deg, #E8B9A5 0 3px, transparent 3px 6px)";

type BarShapeProps = ComponentProps<typeof Rectangle> & {
  payload?: { open?: number };
};

function formatEur(cents: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function downloadPortfolioCsv(
  rows: PortfolioRow[],
  periodLabel: string,
  t: (key: string, fallback?: string) => string
) {
  const header = [
    t("owner.analytics.csvProperty", "Property"),
    t("owner.analytics.csvCity", "City"),
    t("owner.analytics.csvRooms", "Rooms"),
    t("owner.analytics.csvOccupancyPct", "Occupancy %"),
    `${t("owner.analytics.csvDue", "Rent due")} (${periodLabel})`,
    t("owner.analytics.csvCollected", "Collected"),
    t("owner.analytics.csvOpen", "Open"),
    t("owner.analytics.csvYieldPct", "Yield %"),
  ];
  const lines = [
    header.join(","),
    ...rows.map((r) =>
      [
        JSON.stringify(r.title),
        JSON.stringify(r.city),
        `${r.occupied_rooms}/${r.total_rooms}`,
        String(r.occupancy_pct),
        (r.expected_rent_cents / 100).toFixed(2),
        (r.revenue_period_cents / 100).toFixed(2),
        (r.open_cents / 100).toFixed(2),
        r.yield_pct != null ? String(r.yield_pct) : "",
      ].join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `owner-portfolio-${periodLabel.replace(/\s+/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** First day of `offset` months before the current one, as YYYY-MM-01. */
function monthKey(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - offset);
  // Built from the parts rather than toISOString(), which converts to UTC and
  // would hand back the PREVIOUS month for anyone east of Greenwich on the 1st.
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-01`;
}

export function AdminOwnerPortfolio() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [revenueRange, setRevenueRange] = useState<RevenueRange>("month");
  const [propertyFilter, setPropertyFilter] = useState<string>("all");
  const chartLocale = language === "de" ? "de-DE" : "en-GB";
  const chartEuro = (v: number) =>
    v.toLocaleString(chartLocale, { maximumFractionDigits: 0 }) + " €";

  /**
   * V3 swaps the range select for a month picker and makes the chart bars
   * select the same thing. Both presentations live in this one component
   * rather than a fork: every figure, the gating notice and the CSV are
   * identical, and two copies of that would drift the moment either is
   * touched. The flag only decides which controls and which columns render.
   */
  const v3 = useOwnerPortalV3();

  /** Last 12 months, newest first — what the picker offers and the chart shows. */
  const monthOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => monthKey(i)),
    []
  );
  const [selectedMonth, setSelectedMonth] = useState<string>(() => monthKey(0));

  const monthLabel = (iso: string, opts?: Intl.DateTimeFormatOptions) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(chartLocale, {
      month: "long",
      year: "numeric",
      ...opts,
    });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      "owner-portfolio-dashboard",
      revenueRange,
      v3 ? selectedMonth : null,
    ],
    queryFn: async () => {
      const { data: raw, error: rpcError } = await supabase.rpc(
        "owner_portfolio_dashboard",
        v3
          ? { _revenue_range: "month", _month: selectedMonth }
          : { _revenue_range: revenueRange }
      );
      if (rpcError) throw rpcError;
      return normalizePortfolio(raw as PortfolioPayload | null);
    },
  });

  const propertyIds = useMemo(
    () => (data?.properties ?? []).map((p) => p.property_id),
    [data?.properties]
  );

  // owner_portfolio_dashboard scopes purely by property_owners, so a
  // self-submitted-but-not-yet-approved property, or one still waiting on the
  // owner's signed management contract, shows up here exactly like a live
  // one. Both states are surfaced as a disabled, labeled dropdown entry with
  // a link back to "My Properties" to finish onboarding — never silently
  // treated as normal.
  const { data: approvalByPropertyId } = useQuery({
    queryKey: ["owner-portfolio-approval-status", propertyIds],
    enabled: propertyIds.length > 0,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("properties")
        .select("id, approval_status")
        .in("id", propertyIds);
      if (error) throw error;
      const m = new Map<number, string>();
      for (const r of (rows ?? []) as Array<{
        id: number;
        approval_status: string;
      }>) {
        m.set(r.id, r.approval_status);
      }
      return m;
    },
  });
  const { data: contractGateByPropertyId } = useOwnerPropertiesContractGate();

  const propertyGating = useMemo(() => {
    const m = new Map<number, string>();
    for (const id of propertyIds) {
      const approval = approvalByPropertyId?.get(id);
      if (approval === "pending") {
        m.set(
          id,
          t("owner.analytics.pendingApproval", "Pending admin approval")
        );
        continue;
      }
      if (approval === "rejected") {
        m.set(id, t("owner.analytics.changesRequested", "Changes requested"));
        continue;
      }
      if (approval === "withdrawn") {
        m.set(id, t("owner.analytics.withdrawn", "Withdrawn"));
        continue;
      }
      const gate = contractGateByPropertyId?.get(id);
      if (gate && !gate.enabled) {
        m.set(id, t("owner.analytics.pendingSignature", "Pending signature"));
      }
    }
    return m;
  }, [propertyIds, approvalByPropertyId, contractGateByPropertyId, t]);

  // Gated properties are excluded from every figure on this page, which is
  // what the amber notice below has always claimed and what was not true:
  // owner_portfolio_dashboard scopes purely by property_owners and applies no
  // approval_status filter, so a pending property was summed into the tiles,
  // the table, the Total row and the CSV. A property awaiting approval
  // usually has no bookings, so it contributed 0 revenue while still
  // contributing its rooms -- dragging occupancy down with rooms the owner
  // cannot let yet, under a sentence promising it had been left out.
  //
  // Excluded here rather than in the RPC so the dropdown can still name them
  // (that is how the owner gets back to finishing onboarding) and so this
  // needs no migration.
  const visibleRows = useMemo(() => {
    const props = data?.properties ?? [];
    if (propertyGating.size === 0) return props;
    return props.filter((r) => !propertyGating.has(r.property_id));
  }, [data?.properties, propertyGating]);

  const filteredRows = useMemo(() => {
    if (propertyFilter === "all") return visibleRows;
    const id = Number(propertyFilter);
    // A gated property is never selectable (its dropdown entry is disabled),
    // but filter the full set so a stale selection still resolves rather than
    // silently showing the whole portfolio.
    return (data?.properties ?? []).filter((r) => r.property_id === id);
  }, [data?.properties, visibleRows, propertyFilter]);

  /** The bold Total row: sums, not averages. See the row for why. */
  const totals = useMemo(() => {
    const rows = filteredRows;
    const rooms = rows.reduce((a, r) => a + r.total_rooms, 0);
    const occupied = rows.reduce((a, r) => a + r.occupied_rooms, 0);
    return {
      rooms,
      occupied,
      expectedCents: rows.reduce((a, r) => a + r.expected_rent_cents, 0),
      collectedCents: rows.reduce((a, r) => a + r.revenue_period_cents, 0),
      openCents: rows.reduce((a, r) => a + r.open_cents, 0),
      occupancyPct: rooms > 0 ? Math.round((occupied / rooms) * 1000) / 10 : 0,
    };
  }, [filteredRows]);

  const chartData = useMemo(() => {
    const rows = data?.monthly ?? [];
    return rows.map((r) => {
      const d = new Date(r.month);
      const label = d
        .toLocaleDateString(chartLocale, { month: "short", year: "2-digit" })
        .replace(" ", " '");
      const due = Math.round(r.due_cents / 100);
      return {
        label,
        month: r.month,
        // One bar per DUE month: what has been paid of it, and what is open.
        rent: Math.round(r.rent_cents / 100),
        open: Math.round(r.open_cents / 100),
        // Only the selected bar prints its value above itself. A number over
        // every bar is twelve numbers competing with the one being read.
        selectedDue: r.month === selectedMonth ? due : null,
      };
    });
  }, [data?.monthly, chartLocale, selectedMonth]);

  const periodSubtitle = useMemo(() => {
    if (v3) return monthLabel(selectedMonth);
    if (revenueRange === "month")
      return t("owner.analytics.periodMonth", "This month");
    if (revenueRange === "quarter")
      return t("owner.analytics.periodQuarter", "Last 3 months");
    return t("owner.analytics.periodYear", "This year");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueRange, t, v3, selectedMonth, chartLocale]);

  const periodLabelLong = useMemo(() => {
    if (v3) return monthLabel(selectedMonth);
    if (revenueRange === "month")
      return t("owner.analytics.periodMonthLong", "this month");
    if (revenueRange === "quarter")
      return t("owner.analytics.periodQuarterLong", "last 3 months");
    return t("owner.analytics.periodYearLong", "this year");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueRange, t, v3, selectedMonth, chartLocale]);

  const kpis = data?.kpis;

  // The property filter above only ever sliced `filteredRows` for the table
  // and CSV export — the KPI tiles kept reading the portfolio-wide `kpis`
  // object no matter what was selected. `properties[]` rows now carry a
  // per-property equivalent of every kpis field (20260822000000), so a
  // single-property selection maps its row into the same shape the tiles
  // already expect; "all properties" keeps using the portfolio kpis as-is.
  const displayKpis = useMemo(() => {
    if (!kpis) return kpis;
    if (propertyFilter === "all") {
      // Nothing gated: the RPC's own portfolio totals already describe
      // exactly the visible set, so leave them untouched.
      if (propertyGating.size === 0) return kpis;
      // Otherwise rebuild them from the visible rows. The sums are exact and
      // the ratios are derived from those sums rather than averaged, so they
      // stay consistent with the Total row underneath.
      const sum = (pick: (r: PortfolioRow) => number) =>
        visibleRows.reduce((a, r) => a + pick(r), 0);
      const totalRooms = sum((r) => r.total_rooms);
      const occupiedRooms = sum((r) => r.occupied_rooms);
      const collected = sum((r) => r.revenue_period_cents);
      const expected = sum((r) => r.expected_rent_cents);
      // avg_yield_pct is NOT recomputed here.
      //
      // The RPC defines it as sum(annual_net) / sum(invested_amount) -- a
      // CAPITAL-weighted figure -- and `properties[]` exposes neither of those
      // terms, only the per-property ratio. Any weighting available on this
      // side (by rooms, by expected rent, a flat mean) is a different metric
      // wearing the same label, which is worse than the inclusion it would be
      // correcting.
      //
      // It also needs correcting less than the others: apt_yield_base filters
      // on `invested_amount IS NOT NULL AND invested_amount > 0`, so a
      // property with no invested amount -- which a freshly submitted one
      // normally is -- already contributes nothing. A gated property that DOES
      // carry an invested amount still counts; excluding it means filtering
      // `ids` inside the RPC, which is a migration and a separate change.
      return {
        rent_collected_cents: collected,
        occupancy_pct:
          totalRooms > 0
            ? Math.round((occupiedRooms / totalRooms) * 1000) / 10
            : 0,
        occupied_rooms: occupiedRooms,
        total_rooms: totalRooms,
        collection_rate_pct:
          expected > 0 ? Math.round((collected / expected) * 1000) / 10 : null,
        expected_rent_cents: expected,
        open_cents: sum((r) => r.open_cents),
        open_tenants: sum((r) => r.open_tenants),
        avg_yield_pct: kpis.avg_yield_pct,
        overdue_cents: sum((r) => r.overdue_cents),
        overdue_tenants: sum((r) => r.overdue_tenants),
        range: kpis.range,
      };
    }
    const row = filteredRows[0];
    if (!row) return kpis;
    return {
      rent_collected_cents: row.revenue_period_cents,
      occupancy_pct: row.occupancy_pct,
      occupied_rooms: row.occupied_rooms,
      total_rooms: row.total_rooms,
      collection_rate_pct: row.collection_rate_pct,
      expected_rent_cents: row.expected_rent_cents,
      open_cents: row.open_cents,
      open_tenants: row.open_tenants,
      avg_yield_pct: row.yield_pct,
      overdue_cents: row.overdue_cents,
      overdue_tenants: row.overdue_tenants,
      range: kpis.range,
    };
  }, [propertyFilter, filteredRows, visibleRows, propertyGating, kpis]);

  if (isLoading && !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-9 w-72 rounded-lg bg-muted" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-muted" />
          ))}
        </div>
        <div className="h-72 rounded-2xl bg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {isError ? (
        <Alert variant="destructive" className="rounded-xl">
          <AlertTitle>
            {t(
              "owner.analytics.loadErrorTitle",
              "Could not load portfolio analytics"
            )}
          </AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : t(
                  "owner.analytics.loadErrorHint",
                  "Apply the latest Supabase migrations so the owner_portfolio_dashboard function exists, then retry."
                )}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <h1 className={PAGE_TITLE_CLASS}>
            {t("admin.nav.ownerAnalytics", "Analytics & reporting")}
          </h1>
          {/* The "Performance across N properties · All properties" line the
              design drops: the property filter beside it already states the
              scope, so it was the same fact twice. The gating notice below is
              NOT the same fact and stays. */}
          {propertyGating.size > 0 && (
            <p className="text-xs text-amber-600">
              {propertyGating.size === 1
                ? t(
                    "owner.analytics.gatedCountOne",
                    "1 property is pending admin approval or your signature and isn't counted above."
                  )
                : t(
                    "owner.analytics.gatedCountMany",
                    "{n} properties are pending admin approval or your signature and aren't counted above."
                  ).replace("{n}", String(propertyGating.size))}{" "}
              <button
                type="button"
                onClick={() => navigate("/admin/properties")}
                className="font-medium underline underline-offset-2 hover:text-amber-700"
              >
                {t("owner.analytics.completeOnboarding", "Complete onboarding")}
              </button>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={propertyFilter} onValueChange={setPropertyFilter}>
            <SelectTrigger className="h-10 w-[168px] rounded-xl border-border/80 bg-background shadow-sm">
              <SelectValue
                placeholder={t(
                  "owner.analytics.filterPlaceholder",
                  "Properties"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("owner.analytics.allProperties", "All properties")}
              </SelectItem>
              {(data?.properties ?? []).map((p) => {
                const gatedLabel = propertyGating.get(p.property_id);
                return (
                  <SelectItem
                    key={p.property_id}
                    value={String(p.property_id)}
                    disabled={!!gatedLabel}
                    className={gatedLabel ? "opacity-70" : undefined}
                  >
                    {/* Not-yet-live properties stay listed — the owner should
                        see everything they hold — but the name is blurred to
                        read as unavailable. The reason stays crisp, or the
                        entry would just look broken. */}
                    <span className={gatedLabel ? "blur-[1.5px]" : undefined}>
                      {p.title}
                    </span>
                    {gatedLabel ? (
                      <span className="text-muted-foreground">
                        {" "}
                        — {gatedLabel}
                      </span>
                    ) : null}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* V3: one month, not a span. The old range select and the 12-month
              chart answered two different questions on one page — the chart
              ignored both filters — so the picker and the chart now move
              together and a bar is just another way to pick. */}
          {v3 ? (
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="h-10 w-[172px] rounded-xl border-border/80 bg-background shadow-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m} value={m}>
                    {monthLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select
              value={revenueRange}
              onValueChange={(v) => setRevenueRange(v as RevenueRange)}
            >
              <SelectTrigger className="h-10 w-[148px] rounded-xl border-border/80 bg-background shadow-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">
                  {t("owner.analytics.periodMonth", "This month")}
                </SelectItem>
                <SelectItem value="quarter">
                  {t("owner.analytics.periodQuarter", "Last 3 months")}
                </SelectItem>
                <SelectItem value="year">
                  {t("owner.analytics.periodYear", "This year")}
                </SelectItem>
              </SelectContent>
            </Select>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-10 gap-2 rounded-xl shadow-sm"
              >
                <Download className="h-4 w-4" />
                {t("owner.analytics.export", "Export")}
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem
                onClick={() =>
                  downloadPortfolioCsv(filteredRows, periodLabelLong, t)
                }
              >
                {t("owner.analytics.exportCsv", "Property table (CSV)")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div
        className={
          v3
            ? "grid gap-x-8 gap-y-7 border-b border-[#ECE7DD] pb-7 sm:grid-cols-2 xl:grid-cols-4"
            : "grid gap-4 md:grid-cols-2 xl:grid-cols-5"
        }
      >
        <KpiCard
          title={t("owner.analytics.kpiRentCollected", "Rent collected")}
          value={formatEur(displayKpis?.rent_collected_cents ?? 0)}
          // V3 drops the Collection rate tile, so the EXPECTED figure moves
          // here rather than being lost with it: "4.310 € collected" alone
          // does not say whether that is all of it.
          hint={
            v3
              ? t("owner.analytics.kpiOfExpected", "of {x} expected").replace(
                  "{x}",
                  formatEur(displayKpis?.expected_rent_cents ?? 0)
                )
              : periodSubtitle
          }
          flat={v3}
        />
        <KpiCard
          title={t("owner.analytics.kpiOccupancy", "Occupancy")}
          value={`${displayKpis?.occupancy_pct ?? 0}%`}
          hint={`${displayKpis?.occupied_rooms ?? 0} ${t("owner.analytics.of", "of")} ${displayKpis?.total_rooms ?? 0} ${t("owner.analytics.rooms", "rooms")}`}
          flat={v3}
        />
        {!v3 && (
          <KpiCard
            title={t("owner.analytics.kpiCollectionRate", "Collection rate")}
            value={
              displayKpis?.collection_rate_pct != null
                ? `${displayKpis.collection_rate_pct}%`
                : "—"
            }
            hint={`${t("owner.analytics.kpiExpectedPrefix", "Expected")} ${formatEur(displayKpis?.expected_rent_cents ?? 0)}`}
          />
        )}
        <KpiCard
          title={
            <span className="inline-flex items-center gap-1">
              {t("owner.analytics.kpiAvgYield", "Avg. yield")}
              <Info className="h-3.5 w-3.5 opacity-50" aria-hidden />
            </span>
          }
          value={
            displayKpis?.avg_yield_pct != null
              ? `${displayKpis.avg_yield_pct}%`
              : "—"
          }
          hint={t(
            "owner.analytics.kpiAvgYieldHint",
            "annualised vs. purchase price"
          )}
          flat={v3}
        />
        <KpiCard
          title={t("owner.analytics.kpiOpen", "Open")}
          value={formatEur(displayKpis?.open_cents ?? 0)}
          hint={
            // Open is due minus collected for the chosen month, so it holds for
            // a past month too. The overdue part is what is already past its
            // due date: the urgent slice of an open figure that is otherwise
            // just "not paid yet".
            (displayKpis?.open_cents ?? 0) > 0
              ? `${displayKpis?.open_tenants ?? 0} ${
                  displayKpis?.open_tenants === 1
                    ? t("owner.analytics.kpiOpenTenant", "tenant")
                    : t("owner.analytics.kpiOpenTenants", "tenants")
                } · ${t(
                  "owner.analytics.kpiOpenOverdue",
                  "of which overdue {x}"
                ).replace("{x}", formatEur(displayKpis?.overdue_cents ?? 0))}`
              : t("owner.analytics.kpiOpenNone", "Nothing open")
          }
          flat={v3}
          // Terracotta only when there IS something overdue: open rent that is
          // not yet due is information, not an alarm.
          valueClass={
            (displayKpis?.overdue_cents ?? 0) > 0
              ? v3
                ? "text-[#C0714F]"
                : "text-red-600"
              : undefined
          }
        />
      </div>

      <Card
        className={
          v3
            ? "rounded-none border-0 bg-transparent shadow-none"
            : "rounded-2xl border-border/60 shadow-sm overflow-hidden"
        }
      >
        <CardHeader
          className={v3 ? "px-0 pb-4 pt-0" : "pb-2 border-b bg-muted/15"}
        >
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle
                className={
                  v3
                    ? "text-[15px] font-semibold text-[#0F4C4A]"
                    : "text-lg font-semibold"
                }
              >
                {t("owner.analytics.revenueTrend", "Revenue trend")}
              </CardTitle>
              {!v3 && (
                <CardDescription>
                  {t(
                    "owner.analytics.revenueTrendDescDue",
                    "Rent per due month, collected and open, last 12 months."
                  )}
                </CardDescription>
              )}
            </div>
            <div
              className={cn(
                "flex items-center gap-4 text-xs",
                v3 ? "text-[#7A8E8C]" : "text-muted-foreground"
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: v3 ? "#2FB3AA" : "#2DD4BF" }}
                />
                {t("owner.analytics.legendCollected", "Collected")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-sm border border-[#D4906F]"
                  style={{ backgroundImage: OPEN_HATCH_CSS }}
                />
                {t("owner.analytics.legendOpen", "Open")}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent
          className={v3 ? "px-0 pb-0 pt-0 h-[300px]" : "pt-6 h-[320px]"}
        >
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-16">
              {t(
                "owner.analytics.chartEmptyDue",
                "No rent due in the last 12 months yet."
              )}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={chartEuro}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={64}
                />
                <Tooltip
                  cursor={v3 ? { fill: "rgba(15,76,74,0.04)" } : undefined}
                  formatter={(value: number, name: string) => [
                    `€${value.toLocaleString("de-DE")}`,
                    name === "open"
                      ? t("owner.analytics.legendOpen", "Open")
                      : t("owner.analytics.legendCollected", "Collected"),
                  ]}
                  labelFormatter={(l) => String(l)}
                  contentStyle={{ borderRadius: 12 }}
                />
                <defs>
                  <pattern
                    id="owner-open-hatch"
                    width="6"
                    height="6"
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(-45)"
                  >
                    <rect width="3" height="6" fill="#E8B9A5" />
                  </pattern>
                </defs>
                {/* Two stacked series per DUE month: what has been paid of that
                    month's rent, and what is still open. In V3 a bar is a
                    second way to pick the month: the selected one is solid
                    teal with its total above it, the rest are pale. Recharts
                    gives the datum to onClick, so the handler reads the month
                    off the row rather than off an index. */}
                <Bar
                  dataKey="rent"
                  stackId="due"
                  fill={v3 ? "#2FB3AA" : "#2DD4BF"}
                  isAnimationActive={!v3}
                  shape={(props: BarShapeProps) => (
                    <Rectangle
                      {...props}
                      radius={(props.payload?.open ?? 0) > 0 ? 0 : [4, 4, 0, 0]}
                    />
                  )}
                  onClick={
                    v3
                      ? (entry: { month?: string }) =>
                          entry?.month && setSelectedMonth(entry.month)
                      : undefined
                  }
                  className={v3 ? "cursor-pointer" : undefined}
                >
                  {v3 &&
                    chartData.map((d) => (
                      <Cell
                        key={d.month}
                        fill={d.month === selectedMonth ? "#2FB3AA" : "#C9E5E1"}
                      />
                    ))}
                </Bar>
                <Bar
                  dataKey="open"
                  stackId="due"
                  fill="url(#owner-open-hatch)"
                  stroke="#D4906F"
                  strokeWidth={0.5}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={!v3}
                  onClick={
                    v3
                      ? (entry: { month?: string }) =>
                          entry?.month && setSelectedMonth(entry.month)
                      : undefined
                  }
                  className={v3 ? "cursor-pointer" : undefined}
                >
                  {v3 && (
                    <LabelList
                      dataKey="selectedDue"
                      position="top"
                      offset={6}
                      className="fill-[#0F4C4A]"
                      fontSize={11}
                      fontWeight={600}
                      formatter={(v: number | null) =>
                        v == null ? "" : chartEuro(v)
                      }
                    />
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card
        className={
          v3
            ? "rounded-none border-0 bg-transparent shadow-none"
            : "rounded-2xl border-border/60 shadow-sm overflow-hidden"
        }
      >
        <CardHeader
          className={v3 ? "px-0 pb-4 pt-0" : "pb-2 border-b bg-muted/15"}
        >
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle
                className={
                  v3
                    ? "text-[15px] font-semibold text-[#0F4C4A]"
                    : "text-lg font-semibold"
                }
              >
                {t(
                  "owner.analytics.propertyPerformance",
                  "Property performance"
                )}
              </CardTitle>
              {!v3 && (
                <CardDescription>
                  {t(
                    "owner.analytics.propertyPerformanceDescV2",
                    "Revenue and occupancy across your portfolio — select a property to view rooms, tenants, documents and move-outs."
                  )}
                </CardDescription>
              )}
            </div>
            <span className="text-sm text-muted-foreground">
              {filteredRows.length}{" "}
              {filteredRows.length === 1
                ? t("owner.analytics.countProperty", "property")
                : t("owner.analytics.countProperties", "properties")}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="font-semibold">
                  {t("owner.analytics.tableProperty", "Property")}
                </TableHead>
                {!v3 && (
                  <TableHead className="font-semibold">
                    {t("owner.analytics.tableCity", "City")}
                  </TableHead>
                )}
                <TableHead className="font-semibold">
                  {t("owner.analytics.tableRooms", "Rooms")}
                </TableHead>
                {/* Due, collected and open are the three figures of ONE ledger:
                    what the property is billed for the selected month, how
                    much of it has been paid (whenever it was paid), and the
                    rest. They add up to the tile above, by construction. */}
                <TableHead className="font-semibold text-right">
                  {t("owner.analytics.tableDueMonth", "Due / month")}
                </TableHead>
                <TableHead className="font-semibold text-right">
                  {t("owner.analytics.tableCollected", "Collected")}
                </TableHead>
                <TableHead className="font-semibold text-right">
                  {t("owner.analytics.tableOpen", "Open")}
                </TableHead>
                <TableHead className="font-semibold w-[180px]">
                  {t("owner.analytics.tableOccupancy", "Occupancy")}
                </TableHead>
                <TableHead className="font-semibold text-right">
                  {t("owner.analytics.tableYield", "Yield")}
                </TableHead>
                <TableHead className="w-10" aria-hidden />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={v3 ? 8 : 9}
                    className="text-center text-muted-foreground py-12"
                  >
                    {t(
                      "owner.analytics.tableEmpty",
                      "No properties linked to your owner account yet."
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow
                    key={row.property_id}
                    className="border-border/60 cursor-pointer transition-colors hover:bg-muted/40"
                    onClick={() =>
                      navigate(`/admin/owner/property/${row.property_id}`)
                    }
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/admin/owner/property/${row.property_id}`);
                      }
                    }}
                  >
                    <TableCell className="font-medium">{row.title}</TableCell>
                    {!v3 && <TableCell>{row.city}</TableCell>}
                    <TableCell className="tabular-nums">
                      {row.occupied_rooms}/{row.total_rooms}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatEur(row.expected_rent_cents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatEur(row.revenue_period_cents)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums",
                        row.open_cents > 0
                          ? "text-[#C0714F]"
                          : "text-muted-foreground"
                      )}
                    >
                      {formatEur(row.open_cents)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-2 flex-1 max-w-[120px] rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-teal-400 transition-all"
                            style={{
                              width: `${Math.min(100, row.occupancy_pct)}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm tabular-nums text-muted-foreground w-10">
                          {row.occupancy_pct}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {row.yield_pct != null ? `${row.yield_pct}%` : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                    </TableCell>
                  </TableRow>
                ))
              )}
              {/* One property is its own total, so the row would just repeat
                  the line above it. Occupancy is recomputed from the room
                  counts rather than averaged across properties: a mean of
                  percentages weights a 1-room building like a 12-room one. */}
              {v3 && filteredRows.length > 1 && (
                <TableRow className="border-border/60 bg-muted/20 font-semibold hover:bg-muted/20">
                  <TableCell>
                    {t("owner.analytics.tableTotal", "Total")}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {totals.occupied}/{totals.rooms}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatEur(totals.expectedCents)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatEur(totals.collectedCents)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatEur(totals.openCents)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {totals.occupancyPct}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {displayKpis?.avg_yield_pct != null
                      ? `${displayKpis.avg_yield_pct}%`
                      : "—"}
                  </TableCell>
                  <TableCell aria-hidden />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  title,
  value,
  hint,
  valueClass,
  flat,
}: {
  title: ReactNode;
  value: string;
  hint: string;
  valueClass?: string;
  /**
   * V3 prints the figures straight onto the page, as the prototype does: a
   * small grey label, the number, a small grey note. Four bordered cards in a
   * row draw four boxes the reader has to look past to compare four numbers —
   * on a page whose whole job is comparing them.
   */
  flat?: boolean;
}) {
  if (flat) {
    return (
      <div>
        <p className="text-[13px] leading-tight text-[#7A8E8C]">{title}</p>
        <p
          className={cn(
            "mt-1.5 text-[26px] font-bold leading-none tracking-tight tabular-nums text-[#0F4C4A]",
            valueClass
          )}
        >
          {value}
        </p>
        <p className="mt-1.5 text-xs text-[#7A8E8C]">{hint}</p>
      </div>
    );
  }
  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardContent className="p-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">
          {title}
        </p>
        <p
          className={cn(
            "mt-2 text-[26px] font-bold leading-none tabular-nums tracking-tight",
            valueClass
          )}
        >
          {value}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}
